import React, { useState } from 'react';
import { houseIllustrations } from '../data/houseIllustrations';
import HousePrototype from './HousePrototype';
import PurchaseModal from './PurchaseModal';

interface InteractiveHouseSectionProps {
  onNavigate?: (view: 'products' | 'product-selection') => void;
}

const InteractiveHouseSection: React.FC<InteractiveHouseSectionProps> = ({ onNavigate }) => {
  const [selectedHotspot, setSelectedHotspot] = useState<any>(null);
  const [isFlashingHovered, setIsFlashingHovered] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  
  // Get the single comprehensive house illustration
  const houseIllustration = houseIllustrations[0];

  // Hotspots that use flashing
  const flashingHotspotIds = ['chimney-flashing', 'balcony'];

  const handleHotspotClick = (hotspot: any) => {
    setSelectedHotspot(hotspot);
  };

  return (
    <section id="interactive-house" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Explore Critical Waterproofing Areas
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Click on any hotspot to discover how Flexcrete provides maximum protection 
            for all areas of your home - from below-grade foundations to submerged pool applications.
          </p>
        </div>

        {/* House with Left and Right Side Information */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start mb-12">
          {/* Left Side: 3 Informational Boxes */}
          <div className="lg:col-span-3 space-y-4">
            {/* Box 1: For use practically anywhere */}
            <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg p-5 shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🧱</span>
                <h3 className="text-lg font-bold text-gray-900">
                  For use practically anywhere
                </h3>
              </div>
              <ul className="space-y-1.5 text-xs text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Concrete, Gunite, CMU Block</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Tile, Stone, Ceramic</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Metal, Stainless Steel, Galvanized & Corrugated Metal</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>ICF Block, PVC Pipe, Drywall, Cement Board</span>
                </li>
              </ul>
            </div>

{/*         
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">👷</span>
                <h3 className="text-lg font-bold text-gray-900">
                  Anyone can apply
                </h3>
              </div>
              {/* <div className="min-h-[100px] flex items-center justify-center bg-white rounded-md border-2 border-dashed border-gray-300">
                <p className="text-xs text-gray-500 text-center">
                  Video/Pictures will be added here
                </p>
              </div> */}

            {/* Box 3: Applied with household tools */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-5 shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🧰</span>
                <h3 className="text-lg font-bold text-gray-900">
                Anyone can apply with household tools
                </h3>
              </div>
              <ul className="space-y-1.5 text-xs text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Paint Roller</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Paint Brush</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Trowel</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Squeegee</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600 mt-0.5">•</span>
                  <span>Hopper Gun <span className="text-gray-500 italic">(please contact us for guidance)</span></span>
                </li>
              </ul>
            </div>
          </div>

          {/* Center: Interactive House - Larger, Central Focus */}
          <div className="lg:col-span-6">
            <HousePrototype
              illustration={houseIllustration}
              onHotspotClick={handleHotspotClick}
              selectedHotspot={selectedHotspot}
              highlightedHotspotIds={isFlashingHovered ? flashingHotspotIds : []}
            />
          </div>

          {/* Right Side: Flashing and Crack Repair Boxes */}
          <div className="lg:col-span-3 space-y-4">
            {/* Flashing Box */}
            <div 
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 shadow-md transition-all duration-300 hover:shadow-lg"
              onMouseEnter={() => setIsFlashingHovered(true)}
              onMouseLeave={() => setIsFlashingHovered(false)}
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🔧</span>
                <h3 className="text-lg font-bold text-gray-900">
                  Flashing
                </h3>
              </div>
              <p className="text-gray-700 text-sm mb-3">
                Flashing is your primary defense against water intrusion at critical areas. Flexcrete excels at flashing applications where different materials or planes meet - preventing moisture intrusion that can cause wood rot, mildew, and structural damage.
              </p>
              <div className="bg-white rounded-md p-3 border-l-4 border-primary-500">
                <p className="text-xs font-semibold text-gray-800 mb-2">
                  Key Applications:
                </p>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Waterproofing at changes of plane (wall-to-floor, wall-to-deck)</li>
                  <li>• Sealing joints where different materials meet</li>
                  <li>• Prevents water intrusion at critical transitions</li>
                  <li>• Protects against wood rot and mildew growth</li>
                </ul>
              </div>
            </div>

            {/* Crack Repair Box */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-5 shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🔨</span>
                <h3 className="text-lg font-bold text-gray-900">
                  Crack Repair
                </h3>
              </div>
              <div className="flex justify-center rounded-md">
                <img
                  src="/assets/new-image.png"
                  alt="Flexcrete waterproof coating applied for crack repair"
                  className="mx-auto h-auto w-full max-h-56 max-w-[17rem] object-contain rounded-md"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Below the House Section - Horizontal Bar */}
        <div className="bg-gradient-to-r from-primary-600 to-blue-600 rounded-lg p-6 text-center shadow-lg mb-12">
          <h3 className="text-2xl font-bold text-white mb-3">
            🌊 Flexcrete — The Ultimate Waterproofing Solution for Any Surface
          </h3>
          <div className="flex justify-center">
            <button 
              className="bg-white text-primary-700 font-semibold px-12 py-3 rounded-lg hover:bg-gray-100 transition-colors shadow-md w-full max-w-xs"
              onClick={() => onNavigate?.('product-selection')}
            >
              Explore Products
            </button>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Flexcrete for Your Waterproofing Needs?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💪</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Maximum Adhesion</h4>
              <p className="text-gray-600 text-sm">
                Flexcrete adheres to all substrates - concrete, CMU blocks, tile, metal, and more. Works on positive and negative water pressure.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🛡️</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Complete Protection</h4>
              <p className="text-gray-600 text-sm">
                UV resistant, immune to freeze-thaw cycles, resists all corrosive agents. Designed for both interior and exterior applications.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✨</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Professional Results</h4>
              <p className="text-gray-600 text-sm">
                Easy to apply with multiple methods. Prevents efflorescence, provides excellent tile substrate, and can be used for submerged applications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        isOpen={isPurchaseModalOpen} 
        onClose={() => setIsPurchaseModalOpen(false)} 
      />
    </section>
  );
};

export default InteractiveHouseSection;
